/* eslint-disable no-console */
import { type Context, createContext, useContext } from 'react'
import { type SetAvatarPayload } from './nativeEvents.js'
import {
  type WebEvent,
  type VibrationType,
  type NavigationInfoPayload,
  type OpenAppSettingsPayload,
  type OpenInAppBrowserPayload,
  type InitiatePaymentPayload,
} from './webEvents.js'

export type ShowNativeBackdropArgs = {
  color: string
  bottomColor?: string
  timeout?: number
  transition?: 'fade'
  visibleOnHeader?: boolean
  visibleOnFooter?: boolean
}

export type ReceiveAvatarHandler = (payload: SetAvatarPayload) => void

export type NativeBridgeContextProps = {
  isTrackingAllowed?: boolean
  postMessage: (event: WebEvent) => void
  isRefreshRequested?: boolean
  downloadFile: (uri: string, fallbackFileName: string) => Promise<void>
  setUserID: (userId: string) => void
  isBackButtonPressed: boolean
  acknowledgeBackButtonPress: () => void
  acknowledgeRefreshAction: () => void
  requestRating: (delayInSeconds: number) => void
  navigationInfoChanged: (payload: NavigationInfoPayload) => void
  vibrate: (type: VibrationType) => void
  setNativeColorScheme: (scheme: string) => void
  isSocialLoginProviderSupported: (provider: string) => boolean
  showNativeBackdrop: (args?: ShowNativeBackdropArgs) => void
  hideNativeBackdrop: () => void
  requestChangeAvatar: () => void
  isAvatarEditSupported: () => boolean
  onReceiveAvatar: (handler: ReceiveAvatarHandler) => void
  isSharingLinkSupported: boolean
  shareLink: (title: string, url: string) => void
  requestPushNotificationPermissions: () => void
  isDeniedPushNotificationPermission: boolean
  acknowledgeDeniedPushNotificationPermission: () => void
  openAppSettings: (payload?: Partial<OpenAppSettingsPayload>) => void
  disablePushNotificationSettings: () => void
  isDisablePushNotificationRequested: boolean
  acknowledgeDisablePushNotifications: () => void
  requestNativeTrackingPermission: () => void
  openInAppBrowser: (payload: OpenInAppBrowserPayload) => void
  initiatePaymentRequest: (payload: InitiatePaymentPayload) => void
  isInAppLinksSupported: () => boolean
  isInitiatePaymentSupported: () => boolean
}

const isDevelopment = process.env.NODE_ENV !== 'production'

function notImplementedMessage(methodName: string) {
  return () => {
    if (isDevelopment) {
      console.warn(`${methodName} not implemented. Missing native bridge?`)
    }
  }
}

export const NativeBridgeContext: Context<NativeBridgeContextProps> = createContext<NativeBridgeContextProps>({
  isTrackingAllowed: undefined,
  postMessage: notImplementedMessage('postMessage'),
  downloadFile: () => Promise.reject(notImplementedMessage('downloadFile')()),
  setUserID: notImplementedMessage('setUserID'),
  isBackButtonPressed: false,
  isRefreshRequested: false,
  acknowledgeBackButtonPress: notImplementedMessage('acknowledgeBackButtonPress'),
  acknowledgeRefreshAction: notImplementedMessage('acknowledgeRefreshAction'),
  requestRating: notImplementedMessage('requestRating'),
  navigationInfoChanged: notImplementedMessage('navigationInfoChanged'),
  vibrate: notImplementedMessage('vibrate'),
  setNativeColorScheme: notImplementedMessage('setNativeColorScheme'),
  isSocialLoginProviderSupported: () => false,
  showNativeBackdrop: notImplementedMessage('showNativeBackdrop'),
  hideNativeBackdrop: notImplementedMessage('hideNativeBackdrop'),
  requestChangeAvatar: notImplementedMessage('requestChangeAvatar'),
  isAvatarEditSupported: () => false,
  onReceiveAvatar: notImplementedMessage('onReceiveAvatar'),
  isSharingLinkSupported: false,
  isInAppLinksSupported: () => false,
  shareLink: notImplementedMessage('shareLink'),
  requestPushNotificationPermissions: notImplementedMessage('requestPushNotificationPermissions'),
  isDeniedPushNotificationPermission: false,
  acknowledgeDeniedPushNotificationPermission: notImplementedMessage('acknowledgeDeniedPushNotificationPermission'),
  openAppSettings: notImplementedMessage('openAppSettings'),
  disablePushNotificationSettings: notImplementedMessage('disablePushNotificationSettings'),
  isDisablePushNotificationRequested: false,
  acknowledgeDisablePushNotifications: notImplementedMessage('acknowledgeDisablePushNotifications'),
  requestNativeTrackingPermission: notImplementedMessage('requestNativeTrackingPermission'),
  openInAppBrowser: notImplementedMessage('openInAppBrowser'),
  initiatePaymentRequest: notImplementedMessage('initiatePaymentRequest'),
  isInitiatePaymentSupported: () => false,
})

export const useNativeBridge = (): NativeBridgeContextProps => useContext(NativeBridgeContext)
